'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from '@akademi/ui';
import { useTranslation } from 'react-i18next';
import { useKindLabel } from '../_kindLabel';
import {
  useBlockControlStamp,
  useBlockRootAttrs,
  useMicroSource,
  type MicroContentItem,
  type MicroContentKind,
  type MicroProgress,
} from '@akademi/site-builder-runtime';
import type {
  MicroContentPopupProps,
  RendererCompletionOptions,
  RendererCompletionPayload,
  RendererProps,
} from './types';
import { CloseButton, ErrorState, FavoriteButton, LoadingState, PopupHeader } from './chrome';
import { dispatchMicroProgressUpdated, dispatchMicroToast } from '../_events';
import { ShortsFeedNav } from './ShortsFeedNav';
import { createKindOrderSequencer, type ShortsSequencer } from './shortsSequencer';
import { PopupItemProvider } from './FilterChips';
import { VideoRenderer } from './renderers/VideoRenderer';
import { ShortsLoadingFrame, ShortsRenderer } from './renderers/ShortsRenderer';
import { QuizRenderer } from './renderers/QuizRenderer';
import { WeeklyReadRenderer } from './renderers/WeeklyReadRenderer';
import { InfoCardsRenderer } from './renderers/InfoCardsRenderer';
import { ChecklistRenderer } from './renderers/ChecklistRenderer';
import { AudioRenderer } from './renderers/AudioRenderer';
import { DownloadRenderer } from './renderers/DownloadRenderer';
import { InteractiveRenderer } from './renderers/InteractiveRenderer';
import { ScenarioRenderer } from './renderers/ScenarioRenderer';
import { LearningPathRenderer } from './renderers/LearningPathRenderer';
import { PollRenderer } from './renderers/PollRenderer';
import { BusinessStoryCardsRenderer } from './renderers/BusinessStoryCardsRenderer';

/** Delay before auto-returning from a completed path step — long
 *  enough for the "step complete" banner to register, and gives the
 *  progress write a head start before the path refetches statuses. */
const STEP_AUTO_RETURN_MS = 900;

/**
 * Modal orchestrator that opens when a user clicks a micro card.
 * Loads the full content via {@code MicroSource.content.fetchById}
 * (so the {@code body} schema is present), then dispatches to the
 * per-kind renderer.
 *
 * <ul>
 *   <li>Mounted to {@code document.body} via portal (escapes
 *       transform/overflow clipping).</li>
 *   <li>Body scroll locked while open.</li>
 *   <li>Escape closes; click outside the inner card closes.</li>
 *   <li>{@code onComplete} fires when the per-kind renderer signals
 *       completion. The popup also fires
 *       {@code MicroSource.progress.update({status: 'completed'})}
 *       when the adapter has a progress sub-adapter, spreading any
 *       {@link RendererCompletionPayload} the renderer attached.</li>
 *   <li>Opening an item (root or path step) sends a best-effort
 *       {@code status: 'in-progress'} ping so abandoning mid-way still
 *       leaves a row; the backend refuses to downgrade COMPLETED.</li>
 *   <li>LEARNING_PATH steps open INSIDE the popup: the path renderer
 *       calls {@code onOpenStep}, the popup swaps to the step's
 *       renderer with a back-to-path pill, and auto-returns (with a
 *       transient banner) once the step completes — legacy parity.</li>
 * </ul>
 */
export function MicroContentPopup({
  item: triggerItem,
  onClose,
  onComplete,
  shortsSequencer,
}: MicroContentPopupProps) {
  const micro = useMicroSource();
  const { t } = useTranslation('site-builder-blocks');
  const kindLabel = useKindLabel();
  // Lazy init keeps a body-carrying trigger item renderable on the very
  // first frame; the trigger effect below covers every later change.
  const [item, setItem] = useState<MicroContentItem | null>(() =>
    triggerItem?.body ? triggerItem : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [progressRow, setProgressRow] = useState<MicroProgress | null>(null);

  // Learning-path in-popup navigation. `step` non-null = the popup is
  // showing a path step instead of the root item; `pathTitle` labels
  // the back pill. Depth is bounded at 1 — step renderers never
  // receive onOpenStep (the admin picker also prevents nested paths).
  const [step, setStep] = useState<{
    pathTitle: string;
    item: MicroContentItem | null;
    error: string | null;
  } | null>(null);
  const [stepDone, setStepDone] = useState(false);
  const stepTokenRef = useRef(0);
  const autoReturnScheduledRef = useRef(false);
  const autoReturnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Id last sent an opened ping — dedupes to once per item-open (reset
  // on close so reopening the same item pings again).
  const openPingRef = useRef<MicroContentItem['id'] | null>(null);

  // Fullscreen shorts reel: scroll down plays the next short, scroll up
  // the previous one. `reelItem` overrides the trigger while the viewer
  // rides the feed; a changed trigger prop (another card clicked, or
  // the popup closed) resets it during render so every effect below
  // sees the right item in the same pass.
  const [reelItem, setReelItem] = useState<MicroContentItem | null>(null);
  // Direction of the last reel move — drives the slide-in of the next short.
  const [reelDirection, setReelDirection] = useState<'next' | 'prev' | undefined>();
  const goReel = (item: MicroContentItem | null, dir: 'next' | 'prev') => {
    if (!item) return;
    setReelDirection(dir);
    setReelItem(item);
  };
  const [lastTrigger, setLastTrigger] = useState(triggerItem);
  if (triggerItem !== lastTrigger) {
    setLastTrigger(triggerItem);
    setReelItem(null);
  }
  const trigger = triggerItem ? (reelItem ?? triggerItem) : null;

  useEffect(() => setMounted(true), []);

  // Clear any pending auto-return timer on unmount.
  useEffect(
    () => () => {
      if (autoReturnTimerRef.current != null) clearTimeout(autoReturnTimerRef.current);
    },
    [],
  );

  // Current favorite state rides the learner's progress row; anonymous
  // sessions resolve null and the heart simply starts unfilled. The whole
  // row is kept, not just the flag — renderers that persist answers in
  // `result` (checklist, poll) restore from it rather than reading again.
  useEffect(() => {
    setFavorite(false);
    setProgressRow(null);
    if (!trigger || !micro?.progress) return;
    let cancelled = false;
    void micro.progress
      .fetchItem(trigger.id)
      .then((row) => {
        if (cancelled) return;
        setProgressRow(row ?? null);
        if (row?.favorite != null) setFavorite(row.favorite);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [trigger, micro]);

  const toggleFavorite = () => {
    if (!item || !micro?.favorites) return;
    const next = !favorite;
    setFavorite(next);
    void micro.favorites
      .toggle({ itemId: item.id })
      .then((serverState) => {
        setFavorite(serverState);
        dispatchMicroProgressUpdated();
        dispatchMicroToast(
          serverState ? t('microChrome.favorite.added') : t('microChrome.favorite.removed'),
          {
            description: t('microChrome.favorite.toastDetail', {
              kind: kindLabel(item.kind),
              title: item.title,
            }),
            tone: serverState ? 'success' : 'neutral',
          },
        );
      })
      .catch(() => setFavorite(!next));
  };

  useEffect(() => {
    // A new active item resets any in-flight path navigation.
    setStep(null);
    setStepDone(false);
    autoReturnScheduledRef.current = false;
    if (autoReturnTimerRef.current != null) {
      clearTimeout(autoReturnTimerRef.current);
      autoReturnTimerRef.current = null;
    }
    if (!trigger) {
      setItem(null);
      openPingRef.current = null;
      return;
    }
    setError(null);
    // Opened ping — abandoning mid-way still leaves an in-progress row.
    // Fire-and-forget; the backend refuses to downgrade a COMPLETED row.
    if (micro?.progress && openPingRef.current !== trigger.id) {
      openPingRef.current = trigger.id;
      // A short counts as watched the moment it opens (product decision:
      // the admin report must show it completed right away); everything
      // else is in-progress until its renderer says otherwise.
      const status = trigger.kind === 'SHORTS' ? 'completed' : 'in-progress';
      void micro.progress
        .update({ itemId: trigger.id, status })
        .then(() => {
          if (status === 'completed') dispatchMicroProgressUpdated();
        })
        .catch(() => undefined);
    }
    if (trigger.body) {
      setItem(trigger);
      return;
    }
    if (!micro) {
      setError(t('microPopup.adapterNotWired'));
      return;
    }
    // Drop the previous item while the detail loads — a reel swap must
    // not keep the old short playing under the loading frame.
    setItem(null);
    let cancelled = false;
    (async () => {
      try {
        const full = await micro.content.fetchById(trigger.id);
        if (cancelled) return;
        if (!full) setError(t('microPopup.notFound'));
        else setItem(full);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trigger, micro, t]);

  useScrollLock(!!triggerItem);

  // The popup portals to document.body. MicroPopupHost puts a synthetic
  // BlockIdentity in context so these hooks stamp data-<ns>-block /
  // data-<ns>-el on the dialog + its controls (close, next/prev, complete,
  // favourite, renderer buttons) for test automation + onboarding.
  const rootAttrs = useBlockRootAttrs();
  const stampRef = useBlockControlStamp<HTMLDivElement>({ portal: true });

  // Escape close.
  useEffect(() => {
    if (!triggerItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [triggerItem, onClose]);

  // "← Back to path": drop the step view and re-mount the path
  // renderer (which refetches per-step progress on mount).
  const returnToPath = useCallback(() => {
    if (autoReturnTimerRef.current != null) {
      clearTimeout(autoReturnTimerRef.current);
      autoReturnTimerRef.current = null;
    }
    stepTokenRef.current++;
    autoReturnScheduledRef.current = false;
    setStep(null);
    setStepDone(false);
  }, []);

  // Path renderer → open one step's content inside the popup.
  const openStep = useCallback(
    (itemId: number | string) => {
      if (!micro || !item) return;
      const token = ++stepTokenRef.current;
      autoReturnScheduledRef.current = false;
      setStepDone(false);
      setStep({ pathTitle: item.title, item: null, error: null });
      // Same opened ping as the root item — a step abandoned mid-way
      // leaves an in-progress row.
      void micro.progress?.update({ itemId, status: 'in-progress' }).catch(() => undefined);
      void micro.content
        .fetchById(itemId)
        .then((full) => {
          if (stepTokenRef.current !== token) return;
          if (!full)
            setStep({ pathTitle: item.title, item: null, error: t('microPopup.notFound') });
          else setStep({ pathTitle: item.title, item: full, error: null });
        })
        .catch((e: unknown) => {
          if (stepTokenRef.current !== token) return;
          setStep({
            pathTitle: item.title,
            item: null,
            error: e instanceof Error ? e.message : String(e),
          });
        });
    },
    [micro, item, t],
  );

  // ---------------------------------------------------------------------------
  // Shorts reel sequencing. The popup never picks "the next short"
  // itself — it asks the {@link ShortsSequencer}: the caller-supplied
  // engine when one was passed to {@code open}, else the default
  // kind-feed order. Rebuilt per popup open so the default engine's
  // feed cache never outlives the session.
  // ---------------------------------------------------------------------------
  const sequencer = useMemo<ShortsSequencer | null>(() => {
    if (!triggerItem) return null;
    if (shortsSequencer) return shortsSequencer;
    return micro ? createKindOrderSequencer(micro) : null;
  }, [shortsSequencer, micro, triggerItem]);

  // Feed navigation applies to root shorts only — a short opened as a
  // learning-path step ends by returning to its path, not by drifting
  // into the feed.
  const shortsFeedActive = !step && trigger?.kind === 'SHORTS';
  const [neighbors, setNeighbors] = useState<{
    prev: MicroContentItem | null;
    next: MicroContentItem | null;
  }>({ prev: null, next: null });

  useEffect(() => {
    // Drop the previous short's neighbours while this one's load: a step
    // taken in that window must not use stale targets.
    setNeighbors({ prev: null, next: null });
    if (!shortsFeedActive || !sequencer || !trigger) return;
    let cancelled = false;
    void Promise.all([sequencer.previous(trigger), sequencer.next(trigger)])
      .then(([prev, next]) => {
        if (!cancelled) setNeighbors({ prev, next });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [shortsFeedActive, sequencer, trigger]);

  // Stable per neighbour set, so a renderer's "advance when the video
  // ends" effect isn't torn down by an unrelated popup re-render.
  const nextItem = shortsFeedActive ? neighbors.next : null;
  const onAdvance = useMemo(
    () => (nextItem ? () => goReel(nextItem, 'next') : undefined),
    // goReel is a plain setter wrapper; nextItem is the only real input.
    [nextItem],
  );

  const handleComplete = useCallback(
    async (payload?: RendererCompletionPayload, options?: RendererCompletionOptions) => {
      const active = step ? step.item : item;
      if (!active) return;
      if (micro?.progress) {
        try {
          await micro.progress.update({ itemId: active.id, status: 'completed', ...payload });
        } catch {
          /* progress is best-effort */
        }
      }
      dispatchMicroProgressUpdated();
      if (step) {
        // Step inside a learning path: transient banner, then auto-return
        // so the path shows its refreshed statuses. Guarded — a renderer
        // may signal completion more than once.
        if (!autoReturnScheduledRef.current) {
          autoReturnScheduledRef.current = true;
          setStepDone(true);
          autoReturnTimerRef.current = setTimeout(() => {
            autoReturnTimerRef.current = null;
            returnToPath();
          }, STEP_AUTO_RETURN_MS);
        }
        return;
      }
      if (item) onComplete?.(item);
      // A terminal "Finish" button has nothing left to show — the popup
      // lingering on a dead last card is what made completing info cards
      // feel broken. Only for root items: the step branch above already
      // returned, because a step ends by going back to its path.
      if (options?.close) onClose();
    },
    [step, item, micro, onComplete, onClose, returnToPath],
  );

  if (!triggerItem || !mounted) return null;

  // Story Cards renders its own 9:16 phone with an in-phone header close.
  // For this kind the popup frame steps back (transparent, height-driven,
  // no scroll) and suppresses its own close so there is exactly one —
  // matching the standalone player. If the author disabled the header /
  // close, the popup close stays as the fallback.
  // Root detail fetch still in flight — the trigger item is body-less,
  // so mounting a renderer with it would flash per-kind empty states.
  const detailPending = !step && !item && !error;
  const activeItem = step ? step.item : (item ?? trigger);
  const activeError = step ? step.error : error;
  const storyMode = !detailPending && activeItem?.kind === 'BUSINESS_STORY_CARDS';
  // Shorts cover the whole screen — an opaque dark stage (the Instagram /
  // Facebook reel view) with the renderer's 9:16 frame centred in it and
  // the reel chrome (close, ↑/↓) pinned to the screen edges. Applies
  // while the detail is still loading too (activeItem falls back to the
  // body-less trigger, which carries the kind), so the stage doesn't
  // flash white before the player mounts.
  const shortsMode = activeItem?.kind === 'SHORTS';
  // The short-video player is widest (info card beside the video); every
  // other kind gets a roomier frame than the old 720px. Shorts have their own stage.
  const frameMaxWidth =
    activeItem?.kind === 'SHORT_VIDEO' ? 'min(1680px, 97vw)' : 'min(1000px, 96vw)';
  const storyPlayer = (
    activeItem?.body as
      | {
          player?: { showHeader?: boolean; showCloseButton?: boolean };
        }
      | undefined
  )?.player;
  const inPhoneClose =
    storyMode && storyPlayer?.showHeader !== false && storyPlayer?.showCloseButton !== false;

  const content = (
    <div
      {...rootAttrs}
      ref={stampRef}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        // The shorts reel covers the whole screen with a near-opaque
        // stage; other kinds keep the translucent scrim.
        background: shortsMode ? 'rgba(2, 4, 10, 0.94)' : 'rgba(15,23,42,0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'grid',
        placeItems: 'center',
        padding: storyMode || shortsMode ? 0 : 'clamp(8px, 2vw, 24px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={
          storyMode || shortsMode
            ? {
                // Both players bring their own frame — the popup steps
                // back to a transparent, size-to-content shell so clicks
                // on the dark stage around the frame still close.
                position: 'relative',
                background: 'transparent',
                borderRadius: 0,
                boxShadow: 'none',
                width: 'auto',
                maxWidth: '100vw',
                maxHeight: '100dvh',
              }
            : {
                position: 'relative',
                width: '100%',
                maxWidth: frameMaxWidth,
                maxHeight: '94vh',
                overflow: 'auto',
                background: 'var(--color-surface-card, #fff)',
                borderRadius: 16,
                boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.18))',
              }
        }
      >
        {!inPhoneClose && !shortsMode && <CloseButton onClose={onClose} />}
        {!inPhoneClose && !shortsMode && !step && item && micro?.favorites && (
          <FavoriteButton favorite={favorite} onToggle={toggleFavorite} />
        )}

        {/* "← Back to path" pill — only while a step is open. Mirrors
            the close button at top-left. */}
        {step && (
          <button
            type="button"
            onClick={returnToPath}
            aria-label={t('microPopup.backToPath')}
            style={{
              position: 'absolute',
              top: 14,
              left: 14,
              padding: '6px 12px 6px 8px',
              borderRadius: 999,
              border: 'none',
              background: 'rgba(13,148,136,0.12)',
              color: '#0d9488',
              cursor: 'pointer',
              zIndex: 5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '13px',
              fontWeight: 600,
              font: 'inherit',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {step.pathTitle || t('microPopup.backToPath')}
          </button>
        )}

        {/* Transient "step complete" banner shown between a step's
            completion and the auto-return to the parent path. */}
        {step && stepDone && (
          <div
            role="status"
            aria-live="polite"
            style={{
              margin: '56px 16px 4px',
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(13,148,136,0.10)',
              color: '#0d9488',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>{t('microPopup.stepComplete')}</span>
          </div>
        )}

        {activeError ? (
          <ErrorState message={activeError} />
        ) : detailPending ? (
          // Keep the header chrome from the (body-less) trigger item so
          // the popup doesn't jump when the full item arrives. Shorts
          // show the dark 9:16 frame instead — a white header block
          // would flash on the fullscreen stage.
          shortsMode ? (
            <ShortsLoadingFrame />
          ) : (
            <>
              <PopupHeader
                kind={triggerItem.kind}
                title={triggerItem.title}
                description={triggerItem.description}
              />
              <LoadingState />
            </>
          )
        ) : !activeItem ? (
          <LoadingState />
        ) : (
          <PopupItemProvider value={{ item: activeItem, onClose }}>
            <Renderer
              // Key on the active item so switching path ↔ step remounts
              // the renderer with fresh per-item state.
              key={String(activeItem.id)}
              item={activeItem}
              onComplete={handleComplete}
              onClose={onClose}
              // The row belongs to the root item — a path step is a
              // different item, so it must not inherit it.
              progress={step ? null : progressRow}
              // Root items only — bounds path nesting at depth 1.
              onOpenStep={step ? undefined : openStep}
              favorite={favorite}
              onToggleFavorite={!step && micro?.favorites ? toggleFavorite : undefined}
              onAdvance={onAdvance}
              reelDirection={reelDirection}
            />
          </PopupItemProvider>
        )}
      </div>

      {/* Reel chrome: close pinned to the screen corner, ↑/↓ on the
          right edge, wheel / swipe / arrow-key gestures. Which short
          those navigate to came from the sequencer above. */}
      {shortsMode && !inPhoneClose && (
        <ShortsFeedNav
          hasPrev={neighbors.prev != null}
          hasNext={neighbors.next != null}
          onPrev={() => goReel(neighbors.prev, 'prev')}
          onNext={() => goReel(neighbors.next, 'next')}
          onClose={onClose}
        />
      )}
    </div>
  );

  return createPortal(content, document.body);
}

function Renderer({
  item,
  onComplete,
  onClose,
  progress,
  onOpenStep,
  favorite,
  onToggleFavorite,
  onAdvance,
  reelDirection,
}: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const common = { item, onComplete, onClose, progress, favorite, onToggleFavorite } as const;
  switch (item.kind as MicroContentKind) {
    case 'SHORT_VIDEO':
      return <VideoRenderer {...common} />;
    case 'SHORTS':
      return <ShortsRenderer {...common} onAdvance={onAdvance} reelDirection={reelDirection} />;
    case 'QUIZ':
      return <QuizRenderer {...common} />;
    case 'WEEKLY_READ':
      return <WeeklyReadRenderer {...common} />;
    case 'INFO_CARDS':
      return <InfoCardsRenderer {...common} />;
    case 'CHECKLIST':
      return <ChecklistRenderer {...common} />;
    case 'AUDIO':
      return <AudioRenderer {...common} />;
    case 'DOWNLOAD':
      return <DownloadRenderer {...common} />;
    case 'INTERACTIVE':
      return <InteractiveRenderer {...common} />;
    case 'SCENARIO':
      return <ScenarioRenderer {...common} />;
    case 'LEARNING_PATH':
      return <LearningPathRenderer {...common} onOpenStep={onOpenStep} />;
    case 'POLL':
      return <PollRenderer {...common} />;
    case 'BUSINESS_STORY_CARDS':
      return <BusinessStoryCardsRenderer {...common} />;
    default:
      return <ErrorState message={t('microPopup.unsupportedKind', { kind: item.kind })} />;
  }
}
